import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { ArrowLeft, Database, Trash2, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

const DatabaseManagementPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [collections, setCollections] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [progress, setProgress] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    loadCollections();
  }, [user, navigate]);

  const loadCollections = async () => {
    try {
      const response = await api.get('/api/admin/database/collections');
      setCollections(response.data);
    } catch (error) {
      console.error('Error loading collections:', error);
      alert('Error cargando colecciones');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    const deletable = collections.filter(c => c.can_delete).map(c => c.name);
    setSelectedCollections(deletable);
  };

  const handleDeselectAll = () => {
    setSelectedCollections([]);
  };

  const handleToggleCollection = (name) => {
    if (selectedCollections.includes(name)) {
      setSelectedCollections(selectedCollections.filter(n => n !== name));
    } else {
      setSelectedCollections([...selectedCollections, name]);
    }
  };

  const handleDelete = async () => {
    if (selectedCollections.length === 0) {
      alert('No hay colecciones seleccionadas');
      return;
    }

    setShowConfirm(false);
    setDeleting(true);
    setProgress([]);

    try {
      const response = await api.post('/api/admin/database/delete-collections', {
        collections: selectedCollections
      });

      setProgress(response.data.results);
      
      // Recargar colecciones
      await loadCollections();
      setSelectedCollections([]);
      
      alert(`✅ Eliminación completada\n\nTotal documentos eliminados: ${response.data.total_deleted}`);
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeleting(false);
    }
  };

  const getTotalDocuments = () => {
    return selectedCollections.reduce((sum, name) => {
      const col = collections.find(c => c.name === name);
      return sum + (col?.count || 0);
    }, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`shadow-sm ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/super-admin')}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <ArrowLeft className={`w-6 h-6 ${isDark ? 'text-white' : 'text-gray-900'}`} />
              </button>
              <div className="flex items-center gap-3">
                <Database className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <div>
                  <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Gestión de Base de Datos
                  </h1>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Super Administrador
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Warning Banner */}
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-semibold text-red-800">⚠️ Zona Peligrosa</h3>
              <p className="text-sm text-red-700 mt-1">
                Esta acción eliminará permanentemente los datos seleccionados. No se puede deshacer.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mb-6 flex gap-4">
          <button
            onClick={handleSelectAll}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Seleccionar Todas
          </button>
          <button
            onClick={handleDeselectAll}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Deseleccionar Todas
          </button>
          <button
            onClick={() => setShowConfirm(true)}
            disabled={selectedCollections.length === 0 || deleting}
            className="ml-auto px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Trash2 className="w-5 h-5" />
            Eliminar Seleccionadas ({selectedCollections.length})
          </button>
        </div>

        {/* Selected Summary */}
        {selectedCollections.length > 0 && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm font-semibold text-yellow-800">
              📊 Se eliminarán {getTotalDocuments().toLocaleString()} documentos de {selectedCollections.length} colecciones
            </p>
          </div>
        )}

        {/* Collections List */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  <th className="w-12 px-6 py-3"></th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                    Colección
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                    Documentos
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {collections.map((col) => {
                  const isSelected = selectedCollections.includes(col.name);
                  const progressItem = progress.find(p => p.collection === col.name);
                  
                  return (
                    <tr
                      key={col.name}
                      className={`${isSelected ? (isDark ? 'bg-blue-900/20' : 'bg-blue-50') : ''} ${!col.can_delete ? 'opacity-50' : ''}`}
                    >
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={!col.can_delete || deleting}
                          onChange={() => handleToggleCollection(col.name)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed"
                        />
                      </td>
                      <td className={`px-6 py-4 ${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>
                        {col.name}
                        {!col.can_delete && (
                          <span className="ml-2 text-xs text-gray-500">(Protegida)</span>
                        )}
                      </td>
                      <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {col.count.toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        {progressItem ? (
                          progressItem.success ? (
                            <span className="flex items-center gap-2 text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              Eliminados: {progressItem.deleted}
                            </span>
                          ) : (
                            <span className="flex items-center gap-2 text-red-600">
                              <XCircle className="w-4 h-4" />
                              Error
                            </span>
                          )
                        ) : (
                          <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Progress */}
        {deleting && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <p className="text-blue-800 font-semibold">Eliminando colecciones...</p>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  ⚠️ Confirmar Eliminación
                </h3>
                <p className="text-sm text-gray-600">
                  Estás a punto de eliminar permanentemente:
                </p>
                <ul className="mt-3 space-y-1">
                  <li className="text-sm font-semibold text-gray-900">
                    • {selectedCollections.length} colecciones
                  </li>
                  <li className="text-sm font-semibold text-gray-900">
                    • {getTotalDocuments().toLocaleString()} documentos totales
                  </li>
                </ul>
                <p className="mt-3 text-sm text-red-600 font-semibold">
                  Esta acción NO se puede deshacer.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
              >
                Sí, Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseManagementPage;
